import Spaces from "@ably/spaces";
import type { ProfileData, SpaceMember, Space } from "@ably/spaces";
import type { Realtime } from "ably";
import { MousePositionEventData, CursorWithDrawingData, DrawingToolData } from "@/models";
import { SingleTabEnforcer } from "@/utils/single-tab-enforcer";

interface RealtimeSpaceOptions {
  getRealtime?: () => Realtime | undefined;
  onMembersUpdate?: (members: SpaceMember[]) => void;
  presenceData?: ProfileData;
  onKickOut?: () => void;
  enableSingleTab?: boolean;
}

export class RealtimeSpace {
  private spaces?: Spaces;
  private spaceName?: string;
  private currentSpace?: Space;
  private cursorSubscription?: any;
  private membersSubscription?: () => void;
  private readonly getRealtime?: () => Realtime | undefined;
  private presenceData?: ProfileData;
  private readonly onMembersUpdate?: (members: SpaceMember[]) => void;
  private readonly cursorListeners = new Set<(payload: MousePositionEventData, senderId?: string) => void>();
  private readonly drawingListeners = new Set<(payload: CursorWithDrawingData, senderId?: string) => void>();
  private readonly singleTabEnforcer?: SingleTabEnforcer;
  private readonly enableSingleTab: boolean;

  constructor(options: RealtimeSpaceOptions) {
    this.getRealtime = options.getRealtime;
    this.presenceData = options.presenceData;
    this.onMembersUpdate = options.onMembersUpdate;
    this.enableSingleTab = options.enableSingleTab ?? false;

    if (this.enableSingleTab && options.onKickOut) {
      this.singleTabEnforcer = new SingleTabEnforcer(options.onKickOut);

      // Merge tab data into presence data
      const tabData = this.singleTabEnforcer.getTabPresenceData();
      this.presenceData = {
        ...this.presenceData,
        ...tabData,
      } as ProfileData;
    }
  }

  async updateSpace(channelName?: string, force = false) {
    if (!channelName) return;

    const realtime = this.getRealtime?.();
    if (!realtime) return;

    if (!this.spaces || this.spaces.client !== realtime) {
      this.spaces = new Spaces(realtime);
    }

    const targetSpaceName = `space-${channelName}`;
    if (!force && this.spaceName === targetSpaceName) {
      return;
    }

    // Set spaceName immediately to prevent race condition with concurrent calls
    this.spaceName = targetSpaceName;

    await this.teardownSpace();

    const space = await this.spaces.get(targetSpaceName);
    this.currentSpace = space;
    this.attachMembersListener(space);

    try {
      await space.enter(this.presenceData ?? {});
      await this.syncMembers(space);

      // Check single tab after entering space
      if (this.enableSingleTab && this.singleTabEnforcer) {
        const currentUserId = (this.presenceData as any)?.userId;
        if (currentUserId) {
          const members = await space.members.getAll();
          this.singleTabEnforcer.checkAndEnforce(members, currentUserId);
        }
      }
    } catch (error) {
      //
    }

    this.cursorSubscription = await space.cursors.subscribe("update", async (cursorUpdate: any) => {
      if (!this.spaces) return;
      const clientId = this.spaces.client.connection.id;
      if (cursorUpdate?.connectionId && cursorUpdate.connectionId === clientId) return;
      const position = cursorUpdate?.position;
      if (!position) return;

      const drawingData = cursorUpdate?.data as DrawingToolData | undefined;

      if (drawingData && (drawingData.toolType === "laser" || drawingData.toolType === "pencil")) {
        const payload: CursorWithDrawingData = {
          x: Math.floor(position.x ?? 0),
          y: Math.floor(position.y ?? 0),
          drawingData,
        };

        this.drawingListeners.forEach((listener) => listener(payload, cursorUpdate?.connectionId));
      } else {
        const payload: MousePositionEventData = {
          x: Math.floor(position.x ?? 0),
          y: Math.floor(position.y ?? 0),
        };

        this.cursorListeners.forEach((listener) => listener(payload, cursorUpdate?.connectionId));
      }
    });
  }

  async sendCursor(payload: MousePositionEventData) {
    if (!this.currentSpace) {
      return;
    }

    if (typeof payload.x !== "number" || typeof payload.y !== "number") {
      return;
    }

    try {
      await this.currentSpace.cursors.set({
        position: { x: payload.x, y: payload.y },
        data: { toolType: "cursor" },
      });
    } catch (error) {
      //
    }
  }

  async sendLaser(payload: CursorWithDrawingData) {
    if (!this.currentSpace) {
      return;
    }

    if (typeof payload.x !== "number" || typeof payload.y !== "number") {
      return;
    }

    try {
      await this.currentSpace.cursors.set({
        position: { x: payload.x, y: payload.y },
        data: {
          toolType: "laser",
          ...payload.drawingData,
        },
      });
    } catch (error) {
      //
    }
  }

  async sendPencil(payload: CursorWithDrawingData) {
    if (!this.currentSpace) {
      return;
    }

    if (typeof payload.x !== "number" || typeof payload.y !== "number") {
      return;
    }

    try {
      await this.currentSpace.cursors.set({
        position: { x: payload.x, y: payload.y },
        data: {
          toolType: "pencil",
          ...payload.drawingData,
        },
      });
    } catch (error) {
      //
    }
  }

  async disconnect() {
    await this.teardownSpace();
    this.spaceName = undefined;
  }

  addCursorListener(listener: (payload: MousePositionEventData, senderId?: string) => void) {
    this.cursorListeners.add(listener);
    return () => this.cursorListeners.delete(listener);
  }

  addDrawingListener(listener: (payload: CursorWithDrawingData, senderId?: string) => void) {
    this.drawingListeners.add(listener);
    return () => this.drawingListeners.delete(listener);
  }

  private async syncMembers(space: Space) {
    try {
      const members = await space.members.getAll();
      this.publishMembersUpdate(members);
      // Check single tab enforcement on member sync
      if (this.enableSingleTab && this.singleTabEnforcer) {
        const currentUserId = (this.presenceData as any)?.userId;
        if (currentUserId) {
          this.singleTabEnforcer.checkAndEnforce(members, currentUserId);
        }
      }
    } catch (error) {
      //
    }
  }

  private publishMembersUpdate(members: SpaceMember[]) {
    this.onMembersUpdate?.(members);
  }

  private attachMembersListener(space: Space) {
    if (this.membersSubscription) {
      this.membersSubscription();
      this.membersSubscription = undefined;
    }

    const handleMemberUpdate = async () => {
      await this.syncMembers(space);
    };

    space.members.subscribe(["enter", "leave", "update", "remove"], handleMemberUpdate);
    this.membersSubscription = () => {
      try {
        space.members.unsubscribe(["enter", "leave", "update", "remove"], handleMemberUpdate);
      } catch (error) {
        //
      }
    };
  }

  private async teardownSpace() {
    if (!this.currentSpace) return;

    const space = this.currentSpace;

    try {
      if (this.cursorSubscription && typeof this.cursorSubscription.unsubscribe === "function") {
        this.cursorSubscription.unsubscribe();
      } else {
        space.cursors.unsubscribe();
      }
    } catch (error) {
      //
    }

    try {
      await space.leave();
    } catch (error) {
      //
    }

    try {
      if (this.membersSubscription) {
        this.membersSubscription();
      } else {
        space.members.unsubscribe();
      }
    } catch (error) {
      //
    }

    this.publishMembersUpdate([]);

    this.currentSpace = undefined;
    this.cursorSubscription = undefined;
    this.membersSubscription = undefined;
  }
}
